import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowRight, ArrowUpRight, Check, Cpu, 
  ShieldCheck, ChevronRight, Video, Radio, Camera, Share2, 
  Palette, Code, Layers, CheckCircle2, ChevronDown, Sparkles
} from 'lucide-react';
import whatsappIcon from '../assets/WhatsappICON.png';
import { useLanguage } from '../context/LanguageContext';
import { services as servicesTr } from '../data/services';
import { servicesEn } from '../data/services.en';
import { projects } from '../data/projects';
import { tr } from '../locales/tr';
import { en } from '../locales/en';
import { trackEvent } from '../utils/analytics';

const ServiceDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = language === 'TR' ? tr.services : en.services;

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  // Active services dataset based on selected language
  const currentServices = language === 'TR' ? servicesTr : servicesEn;

  // Find active service if slug exists
  const activeService = useMemo(() => {
    if (!slug) return null;
    return currentServices.find(s => s.slug === slug);
  }, [slug, currentServices]);

  // Find related projects for the active service
  const serviceProjects = useMemo(() => {
    if (!activeService) return [];
    return projects.filter(p =>
      p.categories.includes(activeService.title) ||
      p.services.includes(activeService.title) ||
      (activeService.slug === 'web-ve-yazilim' && (p.categories.includes("Web Siteleri") || p.categories.includes("Yazılım"))) ||
      (activeService.slug === 'video-produksiyon' && (p.categories.includes("Tanıtım Filmleri") || p.categories.includes("Reklam Filmleri") || p.categories.includes("Fabrika Filmleri"))) ||
      (activeService.slug === 'canli-yayin-program' && (p.categories.includes("Canlı Yayın") || p.categories.includes("Etkinlik & Lansman"))) ||
      (activeService.slug === 'fotograf-produksiyon' && (p.categories.includes("Fotoğraf") || p.categories.includes("Katalog"))) ||
      (activeService.slug === 'dijital-ve-sosyal-medya' && (p.categories.includes("Sosyal Medya") || p.categories.includes("YouTube Projeleri")))
    ).slice(0, 4);
  }, [activeService]);

  const renderServiceIcon = (iconName: string) => {
    switch (iconName) {
      case 'Video': return <Video className="w-5 h-5 text-[#ff7a63]" />;
      case 'Radio': return <Radio className="w-5 h-5 text-[#ff7a63]" />;
      case 'Sparkles': return <Sparkles className="w-5 h-5 text-[#ff7a63]" />;
      case 'Camera': return <Camera className="w-5 h-5 text-[#ff7a63]" />;
      case 'Share2': return <Share2 className="w-5 h-5 text-[#ff7a63]" />;
      case 'Palette': return <Palette className="w-5 h-5 text-[#ff7a63]" />;
      case 'Code': return <Code className="w-5 h-5 text-[#ff7a63]" />;
      default: return <Layers className="w-5 h-5 text-[#ff7a63]" />;
    }
  };

  // JSON-LD Schema for SEO & AI Search Engine Optimization (GEO)
  const structuredData = useMemo(() => {
    if (!activeService) return null;

    const schema: any = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["Service", "ProfessionalService"],
          "@id": `https://www.we24agency.com/hizmetler/${activeService.slug}#service`,
          "name": activeService.title,
          "description": activeService.shortDescription,
          "provider": {
            "@type": "Organization",
            "name": "WE24 Agency",
            "url": "https://www.we24agency.com"
          },
          "areaServed": {
            "@type": "Country",
            "name": "Worldwide"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": activeService.title,
            "itemListElement": activeService.subservices.map(sub => ({
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": sub
              }
            }))
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": language === 'TR' ? "Ana Sayfa" : "Home",
              "item": "https://www.we24agency.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": language === 'TR' ? "Hizmetlerimiz" : "Services",
              "item": "https://www.we24agency.com/hizmetler"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": activeService.title,
              "item": `https://www.we24agency.com/hizmetler/${activeService.slug}`
            }
          ]
        }
      ]
    };

    if (activeService.faqs && activeService.faqs.length > 0) {
      schema["@graph"].push({
        "@type": "FAQPage",
        "mainEntity": activeService.faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      });
    }

    return JSON.stringify(schema);
  }, [activeService, language]);

  // Index View (Overview of all 7 services)
  if (!slug) {
    return (
      <div className="pt-28 pb-20 min-h-screen bg-background">
        <Helmet>
          <title>{language === 'TR' ? "Hizmetlerimiz - WE24 Agency İstanbul" : "Our Services - WE24 Agency Istanbul"}</title>
          <meta
            name="description"
            content={
              language === 'TR'
                ? "Sinematik film prodüksiyonu, çok kameralı canlı yayın rejileri, post-prodüksiyon VFX, fotoğraf çekimi, sosyal medya ve modern web yazılımları."
                : "Cinematic film production, multi-camera live broadcasting, post-production VFX, photography, social media strategy, and custom web software."
            }
          />
          <link rel="canonical" href={language === 'TR' ? "https://www.we24agency.com/hizmetler/" : "https://www.we24agency.com/en/services/"} />
        </Helmet>

        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
            <span className="text-xs uppercase font-bold tracking-[0.25em] text-[#ff7a63] mb-3 block">
              {language === 'TR' ? "360 DERECE KREATİF ÇÖZÜMLER" : "360-DEGREE CREATIVE CAPABILITIES"}
            </span>
            <h1 className="text-4xl sm:text-6xl font-display tracking-tight text-white mb-5 leading-tight">
              {t.title}
            </h1>
            <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">
              {t.subtitle}
            </p>
          </div>

          {/* 7 Core Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {currentServices.map((service, index) => (
              <div
                key={service.id}
                className="group relative flex flex-col justify-between bg-card/40 hover:bg-card/70 border border-white/[0.08] hover:border-[#ff7a63]/40 rounded-3xl p-7 sm:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
              >
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-[#ff7a63]/40 group-hover:bg-[#ff7a63]/10 transition-colors">
                      {renderServiceIcon(service.icon)}
                    </div>
                    <span className="text-xs uppercase tracking-wider text-zinc-400 font-mono font-bold">
                      0{index + 1}
                    </span>
                  </div>

                  <Link to={`/hizmetler/${service.slug}`}>
                    <h2 className="text-xl sm:text-2xl font-display text-white mb-3 group-hover:text-[#ff7a63] transition-colors">
                      {service.title}
                    </h2>
                  </Link>

                  <p className="text-sm text-zinc-300 mb-6 leading-relaxed">
                    {service.shortDescription}
                  </p>

                  {/* Subservices List */}
                  <div className="space-y-2 border-t border-white/[0.08] pt-5 mb-6">
                    {service.subservices.slice(0, 4).map((sub, sIdx) => (
                      <div key={sIdx} className="flex items-center gap-2 text-xs text-zinc-300">
                        <Check className="w-3.5 h-3.5 text-[#ff7a63] shrink-0" />
                        <span className="truncate">{sub}</span>
                      </div>
                    ))}
                    {service.subservices.length > 4 && (
                      <p className="text-[11px] text-[#ff7a63] font-semibold pt-1">
                        + {service.subservices.length - 4} {language === 'TR' ? "daha fazla uzmanlık..." : "more sub-services..."}
                      </p>
                    )}
                  </div>
                </div>

                <Link
                  to={`/hizmetler/${service.slug}`}
                  className="w-full flex items-center justify-between text-xs font-bold uppercase tracking-wider text-white bg-white/5 hover:bg-[#ff7a63] hover:text-white px-5 py-3 rounded-full border border-white/10 hover:border-transparent transition-all"
                >
                  <span>{t.exploreDetails}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            ))}
          </div>

          {/* Bottom Consultation Banner */}
          <div className="mt-16 sm:mt-20 p-8 sm:p-12 rounded-3xl bg-gradient-to-r from-[#071927] to-[#040e16] border border-white/10 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
            <div className="max-w-2xl">
              <h2 className="text-2xl sm:text-3xl font-display text-white mb-2">
                {language === 'TR' ? "Hangi hizmetin markanıza uygun olduğuna emin değil misiniz?" : "Not sure which service fits your project best?"}
              </h2>
              <p className="text-sm text-zinc-300">
                {language === 'TR' 
                  ? "Kreatif direktörlerimiz ve prodüksiyon uzmanlarımızla ücretsiz strateji görüşmesi yapın." 
                  : "Book a complimentary strategic consultation with our creative directors and production team."}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <a
                href="https://wa.me/905452292424"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider transition-all shadow-lg"
              >
                <img src={whatsappIcon} alt="WhatsApp" className="w-4 h-4 object-contain" />
                <span>{language === 'TR' ? "WhatsApp Danışma" : "Chat on WhatsApp"}</span>
              </a>
              <Link
                to="/iletisim"
                className="px-6 py-3 rounded-full bg-white text-zinc-950 hover:bg-zinc-200 font-bold text-xs uppercase tracking-wider transition-all"
              >
                {language === 'TR' ? "Teklif İsteyin" : "Request Proposal"}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If slug was passed but not found
  if (!activeService) {
    return (
      <div className="pt-36 pb-20 min-h-screen bg-background flex flex-col items-center justify-center text-center px-6">
        <h2 className="text-3xl font-display text-white mb-4">{t.notFound}</h2>
        <p className="text-zinc-400 mb-8 max-w-md">
          {language === 'TR' ? "Aradığınız hizmet başlığı bulunamadı veya taşınmış olabilir." : "The service you are looking for could not be found or has moved."}
        </p>
        <button
          onClick={() => navigate('/hizmetler')}
          className="liquid-glass px-6 py-3 rounded-full text-white text-sm font-medium border border-white/10 hover:border-[#ff7a63]"
        >
          {t.allServices}
        </button>
      </div>
    );
  }

  // Detail View for a Specific Service
  return (
    <div className="pt-28 pb-20 min-h-screen bg-background text-foreground">
      <Helmet>
        <title>{activeService.metaTitle || `${activeService.title} - WE24 Agency`}</title>
        <meta
          name="description"
          content={activeService.metaDescription || activeService.shortDescription}
        />
        {activeService.keywords && (
          <meta name="keywords" content={activeService.keywords.join(', ')} />
        )}
        <link rel="canonical" href={language === 'TR' ? `https://www.we24agency.com/hizmetler/${activeService.slug}/` : `https://www.we24agency.com/en/services/${activeService.slug}/`} />
        {structuredData && (
          <script type="application/ld+json">
            {structuredData}
          </script>
        )}
      </Helmet>

      <div className="max-w-7xl mx-auto px-6">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-zinc-400 mb-8 overflow-x-auto whitespace-nowrap">
          <Link to={language === 'TR' ? "/" : "/en"} className="hover:text-white transition-colors">
            {language === 'TR' ? "Ana Sayfa" : "Home"}
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
          <Link to={language === 'TR' ? "/hizmetler" : "/en/services"} className="hover:text-white transition-colors">
            {language === 'TR' ? "Hizmetlerimiz" : "Services"}
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
          <span className="text-[#ff7a63] font-semibold">{activeService.title}</span>
        </nav>

        {/* Hero Section */}
        <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#071927] to-[#040e16] p-8 sm:p-14 mb-16 shadow-2xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#ff7a63]/10 border border-[#ff7a63]/30 text-[#ff7a63] text-xs font-bold uppercase tracking-wider mb-6">
              {renderServiceIcon(activeService.icon)}
              <span>{language === 'TR' ? "WE24 UZMANLIK ALANI" : "WE24 CORE DISCIPLINE"}</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-display text-white tracking-tight leading-[1.1] mb-6">
              {activeService.title}
            </h1>

            <p className="text-base sm:text-xl text-zinc-200 leading-relaxed mb-8">
              {activeService.fullDescription}
            </p>

            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <a
                href="https://wa.me/905452292424"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('whatsapp_click', { placement: 'service_hero', service: activeService.slug })}
                className="flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider shadow-lg transition-all"
              >
                <img src={whatsappIcon} alt="WhatsApp" className="w-4 h-4 object-contain" />
                <span>{language === 'TR' ? "WhatsApp İle Bilgi Alın" : "Inquire via WhatsApp"}</span>
              </a>

              <Link
                to="/iletisim"
                onClick={() => trackEvent('proposal_click', { placement: 'service_hero', service: activeService.slug })}
                className="flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-zinc-950 hover:bg-zinc-200 font-bold text-xs uppercase tracking-wider shadow-lg transition-all"
              >
                <span>{language === 'TR' ? "Proje Teklifi Alın" : "Request a Proposal"}</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Subservices Breakdown Grid */}
        {activeService.subserviceItems && activeService.subserviceItems.length > 0 && (
          <section className="mb-20">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-xs uppercase tracking-[0.25em] text-[#ff7a63] font-bold mb-2 block">
                {language === 'TR' ? "KAPSAMLI HİZMET ÇÖZÜMLERİMİZ" : "CORE CAPABILITIES"}
              </span>
              <h2 className="text-2xl sm:text-4xl font-display text-white">
                {language === 'TR' ? `${activeService.title} Alt Başlıkları ve Uzmanlıklar` : `${activeService.title} - Specialties & Scope`}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeService.subserviceItems.map((sub, idx) => (
                <div
                  key={sub.title}
                  className="bg-card/40 border border-white/[0.08] hover:border-[#ff7a63]/40 rounded-2xl p-6 flex flex-col justify-between transition-all hover:-translate-y-1 group"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-xs font-mono font-bold text-[#ff7a63]">
                        0{idx + 1}
                      </span>
                      <CheckCircle2 className="w-4 h-4 text-zinc-600 group-hover:text-[#ff7a63] transition-colors" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#ff7a63] transition-colors">
                      {sub.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-zinc-300 leading-relaxed mb-4">
                      {sub.description}
                    </p>
                  </div>

                  {sub.keywords && (
                    <div className="flex flex-wrap gap-1.5 pt-3 border-t border-white/[0.06]">
                      {sub.keywords.map((kw) => (
                        <span key={kw} className="text-[10px] text-zinc-300 bg-white/5 border border-white/5 px-2.5 py-0.5 rounded-full font-medium">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SEO In-Depth Article Sections */}
        {activeService.articleSections && activeService.articleSections.length > 0 && (
          <section className="mb-20 bg-card/20 border border-white/[0.08] rounded-3xl p-8 sm:p-12">
            <div className="max-w-4xl mx-auto space-y-10">
              {activeService.articleSections.map((sec, idx) => (
                <article key={idx} className="border-b border-white/[0.06] last:border-0 pb-8 last:pb-0">
                  <h2 className="text-2xl sm:text-3xl font-display text-white mb-4 leading-snug">
                    {sec.heading}
                  </h2>
                  <p className="text-sm sm:text-base text-zinc-300 leading-relaxed mb-4">
                    {sec.content}
                  </p>
                  {sec.subPoints && (
                    <ul className="space-y-2.5 mt-4">
                      {sec.subPoints.map((point, pIdx) => (
                        <li key={pIdx} className="flex items-start gap-3 text-xs sm:text-sm text-zinc-300 leading-relaxed">
                          <Check className="w-4 h-4 text-[#ff7a63] shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Process & Methodology */}
        <section className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs uppercase tracking-[0.25em] text-[#ff7a63] font-bold mb-2 block">
              {language === 'TR' ? "İŞ AKIŞIMIZ" : "OUR METHODOLOGY"}
            </span>
            <h2 className="text-2xl sm:text-4xl font-display text-white">
              {t.processTitle}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {activeService.process.map((step, idx) => {
              const [title, desc] = step.split(': ');
              return (
                <div
                  key={idx}
                  className="relative bg-card/30 border border-white/[0.08] rounded-2xl p-6 flex flex-col justify-between group hover:border-[#ff7a63]/40 transition-colors"
                >
                  <div>
                    <span className="w-8 h-8 rounded-full bg-[#ff7a63]/10 border border-[#ff7a63]/30 flex items-center justify-center text-xs font-bold text-[#ff7a63] mb-4">
                      {idx + 1}
                    </span>
                    <h3 className="text-sm font-bold text-white mb-2 leading-snug">
                      {title}
                    </h3>
                    <p className="text-xs text-zinc-300 leading-relaxed">
                      {desc || title}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Tech Specs & Deliverables Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          {/* Tech Specs */}
          {activeService.techSpecs && (
            <div className="bg-card/30 border border-white/[0.08] rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <Cpu className="w-5 h-5 text-[#ff7a63]" />
                <h2 className="text-xl font-display text-white">
                  {t.techSpecsTitle}
                </h2>
              </div>
              <div className="space-y-6">
                {activeService.techSpecs.map((spec, idx) => (
                  <div key={idx} className="border-b border-white/[0.06] last:border-0 pb-4 last:pb-0">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[#ff7a63] mb-2">
                      {spec.category}
                    </h3>
                    <ul className="space-y-1.5">
                      {spec.items.map((item, iIdx) => (
                        <li key={iIdx} className="text-xs sm:text-sm text-zinc-300 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deliverables */}
          <div className="bg-card/30 border border-white/[0.08] rounded-3xl p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <ShieldCheck className="w-5 h-5 text-[#ff7a63]" />
                <h2 className="text-xl font-display text-white">
                  {t.deliverablesTitle}
                </h2>
              </div>
              <ul className="space-y-3.5">
                {activeService.deliverables.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-xs sm:text-sm text-zinc-200">
                    <Check className="w-4 h-4 text-[#ff7a63] shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-white/[0.08] flex items-center justify-between">
              <span className="text-xs text-zinc-400">
                {language === 'TR' ? "Tüm teslimatlar güvenli bulut arşiv garantisi ile sunulur." : "All master assets delivered with secure cloud archive warranty."}
              </span>
            </div>
          </div>
        </div>

        {/* FAQs Accordion */}
        {activeService.faqs && activeService.faqs.length > 0 && (
          <section className="mb-20 max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-xs uppercase tracking-[0.25em] text-[#ff7a63] font-bold mb-2 block">
                {language === 'TR' ? "MERAK EDİLENLER" : "FAQ"}
              </span>
              <h2 className="text-2xl sm:text-4xl font-display text-white">
                {t.faqTitle}
              </h2>
            </div>

            <div className="space-y-3">
              {activeService.faqs.map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div
                    key={idx}
                    className="border border-white/[0.08] bg-card/25 rounded-2xl overflow-hidden transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left text-sm sm:text-base font-bold text-white hover:text-[#ff7a63] transition-colors"
                      aria-expanded={isOpen}
                    >
                      <span className="pr-4">{faq.question}</span>
                      <ChevronDown className={`w-4 h-4 text-[#ff7a63] shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 text-xs sm:text-sm text-zinc-300 leading-relaxed border-t border-white/[0.04] pt-3">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Related Projects Carousel/Grid */}
        {serviceProjects.length > 0 && (
          <section className="mb-20">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-[#ff7a63] font-bold mb-2 block">
                  {language === 'TR' ? "PORTFOLYO & REFERANSLAR" : "FEATURED CASE STUDIES"}
                </span>
                <h2 className="text-2xl sm:text-4xl font-display text-white">
                  {t.relatedProjectsTitle}
                </h2>
              </div>
              <Link
                to="/projeler"
                className="text-xs font-bold uppercase tracking-wider text-white hover:text-[#ff7a63] inline-flex items-center gap-1 transition-colors"
              >
                <span>{language === 'TR' ? "Tüm Projeler" : "All Projects"}</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceProjects.map((proj) => (
                <Link
                  key={proj.id}
                  to={`/projeler/${proj.slug}`}
                  className="group flex flex-col bg-card/30 border border-white/[0.08] hover:border-[#ff7a63]/40 rounded-2xl overflow-hidden transition-all hover:-translate-y-1"
                >
                  <div className="relative aspect-video overflow-hidden bg-zinc-900">
                    <img
                      src={proj.coverImage}
                      alt={proj.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                      width="960"
                      height="540"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <span className="text-[10px] font-bold uppercase text-white bg-white/20 px-2.5 py-1 rounded-full">
                        {language === 'TR' ? "İncele" : "View"}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 flex flex-col flex-grow justify-between">
                    <div>
                      <span className="text-[9px] uppercase font-bold text-[#ff7a63] tracking-widest block mb-1">
                        {proj.categories[0]}
                      </span>
                      <h3 className="text-xs sm:text-sm font-bold text-white group-hover:text-[#ff7a63] transition-colors line-clamp-1">
                        {proj.title}
                      </h3>
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-2 line-clamp-1">
                      {proj.client}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Global CTA */}
        <div className="rounded-3xl bg-gradient-to-r from-[#ff7a63]/10 via-[#071927] to-[#040e16] border border-[#ff7a63]/30 p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-4xl font-display text-white mb-3">
            {language === 'TR' ? `Markanızı ${activeService.title} İle Zirveye Taşıyalım` : `Elevate Your Brand with ${activeService.title}`}
          </h2>
          <p className="text-sm sm:text-base text-zinc-300 max-w-2xl mx-auto mb-8">
            {language === 'TR' 
              ? "Projenizin hedeflerini konuşmak ve size özel fiyat teklifi hazırlamak için ekibimizle iletişime geçin." 
              : "Contact our executive producers to discuss your project parameters and receive a tailored quote."}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/iletisim"
              className="px-8 py-3.5 rounded-full bg-[#ff7a63] hover:bg-[#ff6247] text-white font-bold text-xs uppercase tracking-wider transition-all shadow-lg inline-flex items-center gap-2"
            >
              <span>{language === 'TR' ? "Hemen İletişime Geçin" : "Get in Touch Now"}</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:+905452292424"
              className="px-8 py-3.5 rounded-full bg-white/5 hover:bg-white/15 border border-white/15 text-white font-bold text-xs uppercase tracking-wider transition-all"
            >
              +90 545 229 24 24
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
